/**
 * Subtle in-game status overlay (Phase 12).
 */
export class HUD {
  /** @param {{ rootSelector: string }} options */
  constructor({ rootSelector }) {
    this._rootSelector = rootSelector;
    /** @type {HTMLElement | null} */
    this._root = null;
    /** @type {HTMLElement | null} */
    this._crosshair = null;
    /** @type {HTMLElement | null} */
    this._hint = null;
    /** @type {HTMLElement | null} */
    this._npcEl = null;
    /** @type {HTMLElement | null} */
    this._fpsEl = null;
    /** @type {HTMLElement | null} */
    this._coordEls = { x: null, y: null, z: null };
    this._visible = false;
    this._fpsSmoothed = 60;
    /** @type {HTMLElement | null} */
    this._wrap = null;
  }

  mount() {
    const root = document.querySelector(this._rootSelector);
    if (!(root instanceof HTMLElement)) {
      throw new Error(`HUD root not found: ${this._rootSelector}`);
    }
    this._root = root;

    const wrap = document.createElement('div');
    wrap.className = 'game-hud';
    wrap.innerHTML = `
      <div class="game-hud-panel game-hud-flicker">
        <div class="game-hud-status">SYSTEM STATUS</div>
        <div class="game-hud-online">ONLINE</div>
        <div class="game-hud-stat" data-hud-npcs>NPCs: 0</div>
        <div class="game-hud-stat" data-hud-fps>FPS: 60</div>
        <div class="game-hud-coords">
          <div class="game-hud-coords-title">Coordinates:</div>
          <div data-hud-x>X 0</div>
          <div data-hud-y>Y 0</div>
          <div data-hud-z>Z 0</div>
        </div>
      </div>
      <div class="game-hud-crosshair" data-hud-crosshair hidden aria-hidden="true"></div>
      <div class="game-hud-hint" data-hud-hint>Click to capture mouse</div>
      <div class="game-hud-interact" data-hud-interact hidden></div>
      <div class="game-hud-event" data-hud-event hidden></div>
      <div class="game-hud-fade" data-hud-fade aria-hidden="true"></div>
    `;

    this._npcEl = wrap.querySelector('[data-hud-npcs]');
    this._fpsEl = wrap.querySelector('[data-hud-fps]');
    this._coordEls.x = wrap.querySelector('[data-hud-x]');
    this._coordEls.y = wrap.querySelector('[data-hud-y]');
    this._coordEls.z = wrap.querySelector('[data-hud-z]');
    this._crosshair = wrap.querySelector('[data-hud-crosshair]');
    this._hint = wrap.querySelector('[data-hud-hint]');
    /** @type {HTMLElement | null} */
    this._eventEl = wrap.querySelector('[data-hud-event]');
    this._interactEl = wrap.querySelector('[data-hud-interact]');
    this._fadeEl = wrap.querySelector('[data-hud-fade]');
    this._eventTimer = null;

    root.appendChild(wrap);
    this._wrap = wrap;
  }

  show() {
    this._visible = true;
    this._wrap?.classList.add('is-visible');
  }

  hide() {
    this._visible = false;
    this._wrap?.classList.remove('is-visible');
  }

  setPointerLocked(locked) {
    if (this._crosshair instanceof HTMLElement) {
      this._crosshair.hidden = !locked;
    }
    if (this._hint instanceof HTMLElement) {
      this._hint.classList.toggle('is-hidden', locked);
    }
  }

  setNpcCount(count) {
    if (this._npcEl) {
      this._npcEl.textContent = `NPCs: ${count}`;
    }
  }

  /**
   * @param {{ x: number; y: number; z: number }} position
   * @param {number} fps
   */
  setTelemetry({ position, fps }) {
    if (fps > 0) {
      this._fpsSmoothed = this._fpsSmoothed * 0.9 + fps * 0.1;
    }
    if (this._fpsEl) {
      this._fpsEl.textContent = `FPS: ${Math.round(this._fpsSmoothed)}`;
    }
    if (this._coordEls.x) {
      this._coordEls.x.textContent = `X ${Math.round(position.x)}`;
    }
    if (this._coordEls.y) {
      this._coordEls.y.textContent = `Y ${Math.round(position.y)}`;
    }
    if (this._coordEls.z) {
      this._coordEls.z.textContent = `Z ${Math.round(position.z)}`;
    }
  }

  /** @param {string} message @param {number} [durationSeconds] */
  /** @param {string | null} text */
  setInteractHint(text) {
    if (!(this._interactEl instanceof HTMLElement)) return;
    if (!text) {
      this._interactEl.hidden = true;
      this._interactEl.textContent = '';
      return;
    }
    this._interactEl.textContent = text;
    this._interactEl.hidden = false;
  }

  /** @param {number} opacity 0–1 */
  setScreenFade(opacity) {
    if (!(this._fadeEl instanceof HTMLElement)) return;
    const clamped = Math.max(0, Math.min(1, opacity));
    this._fadeEl.style.opacity = String(clamped);
    this._fadeEl.classList.toggle('is-active', clamped > 0.01);
  }

  flashWorldEvent(message, durationSeconds = 2) {
    if (!(this._eventEl instanceof HTMLElement)) return;
    this._eventEl.textContent = message;
    this._eventEl.hidden = false;
    this._eventEl.classList.add('is-active');
    if (this._eventTimer) window.clearTimeout(this._eventTimer);
    this._eventTimer = window.setTimeout(() => {
      this._eventEl?.classList.remove('is-active');
      if (this._eventEl) this._eventEl.hidden = true;
      this._eventTimer = null;
    }, durationSeconds * 1000);
  }

  update(_delta) {}

  dispose() {
    if (this._root) {
      this._root.replaceChildren();
    }
    this._root = null;
    this._wrap = null;
    this._crosshair = null;
    this._hint = null;
    this._npcEl = null;
    this._fpsEl = null;
    this._coordEls = { x: null, y: null, z: null };
  }
}
